import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Leaf } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Motion";
import { products } from "@/data/products";

const SectionLabel = ({ children }) => (
  <p className="mb-10 text-xs uppercase tracking-[0.4em] text-gilt">{children}</p>
);

export default function ProductPage() {
  const { slug } = useParams();
  const idx = products.findIndex((p) => p.slug === slug);

  if (idx === -1) {
    return (
      <main data-testid="product-not-found" className="flex min-h-screen flex-col items-center justify-center px-6 pt-24 text-center">
        <h1 className="font-serif text-5xl italic text-ivory">This blend is not on our shelves.</h1>
        <Link data-testid="not-found-home-link" to="/" className="mt-10 border border-gilt px-8 py-4 text-xs uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-gilt hover:text-[#EFE8D2]">
          Return to the atelier
        </Link>
      </main>
    );
  }

  const p = products[idx];
  const next = products[(idx + 1) % products.length];

  return (
    <main data-testid={`product-page-${p.slug}`} className="pt-24">
      <header className="mx-auto max-w-[1600px] px-6 pb-20 pt-16 lg:px-12 lg:pt-24">
        <Reveal y={16}>
          <Link data-testid="product-back-link" to="/" className="mb-12 inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-sand transition-colors duration-300 hover:text-gilt">
            <ArrowLeft size={14} /> The Collection
          </Link>
        </Reveal>
        <div className="flex items-baseline gap-6">
          <span data-testid="product-index" className="font-serif text-xl italic text-gilt">{p.index}</span>
          <p data-testid="product-type" className="text-xs uppercase tracking-[0.35em] text-sand">{p.type}</p>
        </div>
        <h1 className="mt-6 font-serif text-6xl leading-[0.98] text-ivory lg:text-8xl" data-testid="product-name">
          <MaskedLine delay={0.1}>{p.name}</MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p data-testid="product-tagline" className="mt-8 font-serif text-2xl italic text-gilt lg:text-3xl">{p.tagline}</p>
        </Reveal>
      </header>

      <div className="mx-auto grid max-w-[1600px] gap-16 px-6 pb-28 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <Reveal delay={0.2}>
              <p data-testid="product-intro" className="border-l-2 border-gilt pl-6 font-serif text-xl leading-relaxed text-ivory lg:text-2xl">
                {p.intro}
              </p>
              {p.intro2 && (
                <p data-testid="product-intro-2" className="mt-8 border-l-2 border-moss-700 pl-6 text-base leading-relaxed text-sand">
                  {p.intro2}
                </p>
              )}
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-8">
          {p.usage && (
            <section data-testid="product-usage" className="mb-24">
              <Reveal><SectionLabel>How to Use</SectionLabel></Reveal>
              <Reveal>
                <p className="border-l-2 border-gilt pl-6 font-serif text-xl italic leading-relaxed text-ivory lg:text-2xl">
                  {p.usage}
                </p>
              </Reveal>
            </section>
          )}

          {p.usage && p.composition && (
            <section data-testid="product-composition" className="mb-24">
              <Reveal><SectionLabel>{p.compositionLabel || "Blend Composition"}</SectionLabel></Reveal>
              <Reveal>
                <div className="border border-moss-700">
                  {p.composition.map((c) => (
                    <div key={c.name} className="flex items-baseline justify-between gap-6 border-b border-moss-700 px-6 py-4 last:border-b-0">
                      <p className="font-serif text-lg text-ivory">{c.name}</p>
                      <span className="hidden flex-1 border-b border-dashed border-moss-700 sm:block" />
                      <p className="text-sm tracking-widest text-gilt">{c.amount}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </section>
          )}

          {p.table && (
            <section data-testid="product-table" className="mb-24">
              <Reveal><SectionLabel>{p.table.label}</SectionLabel></Reveal>
              <Reveal>
                <div className="overflow-x-auto border border-moss-700">
                  <table className="w-full min-w-[540px] text-left">
                    <thead>
                      <tr className="border-b border-moss-700">
                        {p.table.columns.map((c, i) => (
                          <th key={c} className={`px-6 py-4 text-xs uppercase tracking-[0.25em] ${i === 0 ? "text-gilt" : "text-sand"}`}>
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {p.table.rows.map((row) => (
                        <tr key={row[0]} className="border-b border-moss-700 last:border-b-0">
                          {row.map((cell, i) => (
                            <td key={i} className={i === 0 ? "px-6 py-4 font-serif text-lg text-ivory" : "px-6 py-4 text-sm tracking-widest text-sand"}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Reveal>
            </section>
          )}

          {p.brewing && (
            <section data-testid="product-brewing" className="mb-24">
              <Reveal><SectionLabel>{p.brewing.title}</SectionLabel></Reveal>
              <div className="border border-moss-700">
                {p.brewing.steps.map((step, i) => (
                  <Reveal key={step}>
                    <div data-testid={`brewing-step-${i + 1}`} className="flex items-baseline gap-6 border-b border-moss-700 px-6 py-5 last:border-b-0">
                      <p className="font-serif text-sm italic text-gilt">{String(i + 1).padStart(2, "0")}</p>
                      <p className="text-sm leading-relaxed text-ivory">{step}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              {p.brewing.note && (
                <Reveal>
                  <p data-testid="brewing-note" className="mt-6 border-l-2 border-gilt pl-6 font-serif text-lg italic leading-relaxed text-sand">
                    {p.brewing.note}
                  </p>
                </Reveal>
              )}
            </section>
          )}

          {p.benefits && (
            <section data-testid="product-benefits" className="mb-24">
              <Reveal><SectionLabel>Key Benefits</SectionLabel></Reveal>
              {p.benefits.map((b, i) => (
                <Reveal key={b.title} delay={i * 0.04}>
                  <div className="grid gap-3 border-t border-moss-700 py-8 lg:grid-cols-12">
                    <p className="font-serif text-sm italic text-gilt lg:col-span-2">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="font-serif text-2xl text-ivory lg:col-span-4">{b.title}</h3>
                    <p className="text-sm leading-relaxed text-sand lg:col-span-6">{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </section>
          )}

          {p.highlights && (
            <section data-testid="product-highlights" className="mb-24">
              <Reveal><SectionLabel>Blend Highlights</SectionLabel></Reveal>
              <div className="border border-moss-700">
                {p.highlights.map((h) => (
                  <Reveal key={h}>
                    <div className="flex items-center gap-4 border-b border-moss-700 px-6 py-5 last:border-b-0">
                      <Leaf size={14} className="shrink-0 text-gilt" />
                      <p className="text-sm text-ivory">{h}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </section>
          )}

          <section data-testid="product-ingredients" className="mb-24">
            <Reveal><SectionLabel>Herbal Ingredients</SectionLabel></Reveal>
            {p.ingredients.map((ing) => (
              <Reveal key={ing.name}>
                <div className="border-t border-moss-700 py-10">
                  <h3 className="font-serif text-3xl italic text-ivory">{ing.name}</h3>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-sand">{ing.text}</p>
                  {ing.points && (
                    <ul className="mt-6 grid gap-x-10 gap-y-3 sm:grid-cols-2">
                      {ing.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-3 text-sm text-sand">
                          <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-gilt" />
                          {pt}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </section>

          {!p.usage && p.composition && (
            <section data-testid="product-composition" className="mb-24">
              <Reveal><SectionLabel>{p.compositionLabel || "Blend Composition"}</SectionLabel></Reveal>
              <Reveal>
                <div className="border border-moss-700">
                  {p.composition.map((c) => (
                    <div key={c.name} className="flex items-baseline justify-between gap-6 border-b border-moss-700 px-6 py-4 last:border-b-0">
                      <p className="font-serif text-lg text-ivory">{c.name}</p>
                      <span className="hidden flex-1 border-b border-dashed border-moss-700 sm:block" />
                      <p className="text-sm tracking-widest text-gilt">{c.amount}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </section>
          )}

          {p.preparation && (
            <section data-testid="product-preparation" className="mb-24">
              <Reveal><SectionLabel>{p.preparation.label}</SectionLabel></Reveal>
              {p.preparation.steps.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.04}>
                  <div data-testid={`preparation-step-${i + 1}`} className="grid gap-3 border-t border-moss-700 py-8 lg:grid-cols-12">
                    <p className="font-serif text-sm italic text-gilt lg:col-span-2">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="font-serif text-2xl text-ivory lg:col-span-4">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-sand lg:col-span-6">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </section>
          )}

          {p.formulation && (
            <section data-testid="product-formulation" className="mb-24">
              <Reveal><SectionLabel>The Formulation</SectionLabel></Reveal>
              <Reveal>
                <blockquote className="border-l-2 border-gilt pl-8 font-serif text-xl italic leading-relaxed text-ivory lg:text-2xl">
                  {p.formulation}
                </blockquote>
              </Reveal>
            </section>
          )}

          {p.care && (
            <section data-testid="product-care" className="mb-24">
              <Reveal><SectionLabel>Care & Keeping</SectionLabel></Reveal>
              <div className="border border-moss-700">
                {p.care.map((c) => (
                  <Reveal key={c.title}>
                    <div data-testid={`care-${c.title.toLowerCase()}`} className="border-b border-moss-700 px-6 py-6 last:border-b-0">
                      <p className="text-xs uppercase tracking-[0.3em] text-gilt">{c.title}</p>
                      <p className="mt-3 text-sm leading-relaxed text-ivory">{c.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </section>
          )}

          <Reveal>
            <div data-testid="product-safety" className="border border-giltdeep/60 bg-moss-900 p-8">
              <p className="text-xs uppercase tracking-[0.35em] text-gilt">Safety Notice</p>
              <p className="mt-4 text-sm leading-relaxed text-sand">{p.safety}</p>
            </div>
          </Reveal>
        </div>
      </div>

      <Link
        data-testid="product-next-link"
        to={`/collection/${next.slug}`}
        className="group block border-t border-moss-700/60 bg-moss-900 transition-colors duration-700 hover:bg-moss-800"
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-8 px-6 py-16 lg:px-12 lg:py-20">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-sand">Next in the collection · {next.index}</p>
            <h2 className="mt-4 font-serif text-4xl text-ivory transition-colors duration-500 group-hover:text-gilt lg:text-6xl">
              {next.name}
            </h2>
          </div>
          <span className="flex h-14 w-14 shrink-0 items-center justify-center border border-moss-700 text-sand transition-all duration-500 group-hover:border-gilt group-hover:bg-gilt group-hover:text-[#EFE8D2]">
            <ArrowRight size={20} />
          </span>
        </div>
      </Link>
    </main>
  );
}
